import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw, ImageOff, Save, PlayCircle, FileText, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ImageField } from './AdminCrud';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const KIND_ICON = { image: ImageOff, video: PlayCircle, document: FileText };

const MediaRow = ({ item, onSaved }) => {
  const [value, setValue] = useState(item.url || '');
  const [busy, setBusy] = useState(false);
  const Icon = KIND_ICON[item.kind] || ImageOff;

  const save = async () => {
    if (!item.update_endpoint) return;
    setBusy(true);
    try {
      await api.put(item.update_endpoint, { [item.field]: value });
      toast.success('Updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="media-review-row">
      <CardContent className="p-4">
        <div className="flex items-start gap-4 flex-col sm:flex-row">
          <div className="h-20 w-full sm:w-32 shrink-0 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
            {item.url && item.kind === 'image' ? <img src={fileUrl(item.url)} alt="" className="h-full w-full object-cover" /> : <Icon className="h-8 w-8 text-slate-300" />}
          </div>
          <div className="flex-1 min-w-0 w-full">
            <p className="font-semibold text-[#1a2744] text-sm">{item.label}</p>
            <p className="text-xs text-slate-400 mb-2">{item.collection} · field: {item.field}</p>
            {item.update_endpoint ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1"><ImageField value={value} onChange={setValue} category="replacements" accept={item.kind === 'video' ? 'video/*' : item.kind === 'document' ? '.pdf,.doc,.docx,image/*' : 'image/*'} /></div>
                <Button onClick={save} disabled={busy || value === item.url} className="h-10 bg-[#1a2744] text-white font-bold shrink-0 btn-press" data-testid="media-review-save">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
                </Button>
              </div>
            ) : (
              <Button asChild variant="outline" size="sm" className="font-bold"><Link to={item.edit_link || '/admin/pages'}>Edit in Pages editor</Link></Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AdminMediaReview() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/placeholder-media').then((r) => setData(r.data)).catch(() => toast.error('Failed to load'));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-media-review">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">Replace Placeholder Media</h1>
        <Button variant="outline" onClick={load} className="h-10 font-bold btn-press" data-testid="media-review-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Rescan</Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Every placeholder image, empty video block, and missing document across the site. Replace each one with real campaign media — paste a URL or upload a file, then Save.</p>

      {data.count === 0 ? (
        <Card className="border-emerald-300 bg-emerald-50"><CardContent className="p-8 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
          <p className="font-bold text-emerald-900">No placeholder media remaining — everything has been replaced!</p>
        </CardContent></Card>
      ) : (
        <>
          <p className="font-bold text-[#1a2744] mb-3" data-testid="media-review-count">{data.count} placeholder item(s) found</p>
          <div className="space-y-3">
            {data.items.map((item, i) => <MediaRow key={`${item.collection}-${item.id}-${item.field}-${i}`} item={item} onSaved={load} />)}
          </div>
        </>
      )}
    </div>
  );
}
